import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { ArrowRight, Loader2, LayoutDashboard, Briefcase, Shield } from 'lucide-react';
import { useAuth } from '@/_core/hooks/useAuth';
import { LoginPrompt } from '@/components/auth/AuthDialog';
import HelioLogoMark, { HelioHomeLink } from '@/components/HelioLogoMark';
import { goToPortal } from '@/utils/openPortal';
import {
  allowedPortals,
  defaultPortalPath,
  isDualRole,
  portalLabel,
  setPreferredPortal,
  type PortalKind,
} from '@/utils/portalRouting';

const PORTAL_COPY: Record<PortalKind, { description: string; icon: typeof LayoutDashboard }> = {
  customer: {
    description: 'Track your installation and documents.',
    icon: LayoutDashboard,
  },
  installer: {
    description: 'Manage leads, jobs, and project documents.',
    icon: Briefcase,
  },
  admin: {
    description: 'Oversee users, leads, projects, and applications.',
    icon: Shield,
  },
};

export default function SelectPortal() {
  const { user, loading, isAuthenticated } = useAuth();
  const [, navigate] = useLocation();

  useEffect(() => {
    if (loading || !isAuthenticated || !user) return;
    if (isDualRole(user)) return;
    navigate(defaultPortalPath(user), { replace: true });
  }, [loading, isAuthenticated, user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050d1a] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#29ffa9] animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050d1a] text-white">
        <LoginPrompt message="Sign in to open your portal." />
      </div>
    );
  }

  if (!user || !isDualRole(user)) {
    return (
      <div className="min-h-screen bg-[#050d1a] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#29ffa9] animate-spin" />
      </div>
    );
  }

  const options = allowedPortals(user);

  const choose = (portal: PortalKind) => {
    setPreferredPortal(portal);
    goToPortal(portal, navigate);
  };

  return (
    <div className="min-h-screen bg-[#050d1a] text-white flex flex-col">
      <header className="px-6 py-5 border-b border-white/8">
        <HelioHomeLink className="inline-flex items-center gap-2">
          <HelioLogoMark linked={false} />
        </HelioHomeLink>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-2xl">
          <h1 className="font-display text-2xl font-semibold mb-2">Choose a portal</h1>
          <p className="text-white/50 text-sm mb-8">
            You have a customer quote as well as {user.role === 'admin' ? 'admin' : 'installer'} access.
            Pick which workspace to open.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            {options.map((portal) => {
              const copy = PORTAL_COPY[portal];
              const Icon = copy.icon;
              return (
                <button
                  key={portal}
                  type="button"
                  onClick={() => choose(portal)}
                  className="text-left rounded-2xl border border-white/10 bg-[#0d1b2e] p-6 hover:border-[#29ffa9]/40 hover:bg-[#0d1b2e]/80 transition-colors group"
                >
                  <Icon className="h-8 w-8 text-[#29ffa9] mb-4" />
                  <h2 className="font-display text-lg font-semibold mb-1">{portalLabel(portal)}</h2>
                  <p className="text-sm text-white/50 mb-4">{copy.description}</p>
                  <span className="inline-flex items-center gap-1 text-sm text-[#29ffa9] group-hover:gap-2 transition-all">
                    Continue
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
